// src/pages/Users/Galleries/AddPhotoToAlbumModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload } from "react-icons/fa";
import Button from "../../../components/Button/Button";

// Métadonnées
import {
  addPhoto,
  updatePhoto,
  deletePhoto,
} from "../../../services/photoServices";
// Upload 100% serveur (remplacement par id)
import {
  replaceAlbumPhoto,
  toAbsolutePhotoUrl as toAbsPhoto,
} from "../../../services/albumPhotoServices";

import { getGalleryById } from "../../../services/gallerieServices";
import { toast } from "react-toastify";

const FALLBACK = "/img/no-image.jpg";

export default function AddPhotoToAlbumModal({
  isOpen,
  onClose,
  galleryId,
  photoId,
}) {
  const [currentId, setCurrentId] = useState(photoId ?? null); // id réel de la photo
  const isEdit = Boolean(currentId);

  // form
  const [photoUrl, setPhotoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // ➕ nouveaux champs
  const [position, setPosition] = useState(""); // string pour permettre vide -> "utiliser la suivante"
  const [isCover, setIsCover] = useState(false); // utilisé uniquement à la création

  // upload
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // ui
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const firstFieldRef = useRef(null);

  const initial = useMemo(
    () => ({
      photoUrl: "",
      description: "",
      isActive: true,
      position: "",
      isCover: false,
    }),
    []
  );

  // lock scroll + esc + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    setCurrentId(photoId ?? null);
  }, [photoId]);

  // Pré-remplir la position avec la "prochaine" dispo (max(position)+1)
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) return; // en édition on ne change pas la position par défaut
    (async () => {
      try {
        setLoadingDefaults(true);
        const gal = await getGalleryById(Number(galleryId));
        const photos = Array.isArray(gal?.photos) ? gal.photos : [];
        const maxPos = photos.reduce((m, p) => {
          const pos = Number.isFinite(p?.position) ? p.position : 0; // fallback = 0
          return Math.max(m, pos);
        }, 0); // seed = 0 => si album vide => maxPos=0
        setPosition(String(maxPos + 1)); // => 1 pour la 1re photo
      } catch (e) {
        // si erreur, on laisse vide -> le back mettra la suivante
        setPosition("");
      } finally {
        setLoadingDefaults(false);
      }
    })();
  }, [isOpen, isEdit, galleryId]);

  const isDirty =
    photoUrl !== initial.photoUrl ||
    description !== initial.description ||
    isActive !== initial.isActive ||
    position !== initial.position ||
    isCover !== initial.isCover ||
    !!selectedFile;

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  // Aperçu : object URL si fichier local, sinon URL absolue serveur
  const previewURL = useMemo(() => {
    if (selectedFile) return URL.createObjectURL(selectedFile);
    return toAbsPhoto(photoUrl) || "";
  }, [selectedFile, photoUrl]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) {
      setSelectedFile(f);
    } else {
      toast.warn("Dépose une image valide (JPG, PNG, WebP…).");
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Construit le payload métadonnées
  const buildPayload = (forCreate = false) => {
    const payload = {
      gallerie_id: galleryId,
      description,
      is_active: isActive,
    };
    // position : si vide => ne pas l'envoyer -> le back utilisera la suivante
    if (
      String(position).trim() !== "" &&
      !Number.isNaN(parseInt(position, 10))
    ) {
      payload.position = parseInt(position, 10);
    }
    // is_cover : seulement à la création (l’édition de la couverture se fait sur la page album)
    if (forCreate) {
      payload.is_cover = !!isCover;
    }
    return payload;
  };

  // Étape 1 — UPLOAD (crée d'abord les métadonnées si pas d'id, pour que la position soit respectée)
  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.warn("Choisis une image avant de téléverser.");
      return;
    }
    try {
      setUploading(true);

      // Si pas encore d’ID => on crée d'abord la ligne avec position/is_cover, puis on envoie l'image
      if (!currentId) {
        const created = await addPhoto(buildPayload(true));
        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || "");
        // Ensuite, on remplace le fichier pour cette photo
        if (created?.id) {
          const updated = await replaceAlbumPhoto(
            galleryId,
            created.id,
            selectedFile,
            { format: "webp" }
          );
          setPhotoUrl(updated?.photo_url || created?.photo_url || "");
        }
        setSelectedFile(null);
        toast.success("Image téléversée sur le serveur.");
        return;
      }

      // Sinon, on remplace juste le fichier existant
      const updated = await replaceAlbumPhoto(
        galleryId,
        currentId,
        selectedFile,
        { format: "webp" }
      );
      setPhotoUrl(updated?.photo_url || "");
      setSelectedFile(null);
      toast.success("Image remplacée sur le serveur.");
    } catch (err) {
      console.error(err);
      toast.error("Échec du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  // Étape 2 — ENREGISTRER/mettre à jour les infos (description / actif / position)
  const handleSaveInfos = async () => {
    if (!currentId) {
      try {
        setSaving(true);
        const created = await addPhoto(buildPayload(true));
        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || ""); // souvent vide si pas d'image
        toast.success("Infos enregistrées. Téléverse l’image pour finaliser.");
      } catch (err) {
        console.error(err);
        toast.error("Échec de la création de la photo.");
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      // en édition, on ne touche pas is_cover ici
      await updatePhoto(currentId, buildPayload(false));
      toast.success("Infos mises à jour.");
    } catch (err) {
      console.error(err);
      toast.error("Échec de la sauvegarde des infos.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    onClose?.();
  };

  const handleClose = async () => {
    const noImage = !photoUrl && !selectedFile;
    const noDesc = !description.trim();
    if (currentId && noImage && noDesc) {
      const ok = window.confirm("Annuler et supprimer l’entrée vide ?");
      if (ok) {
        try {
          await deletePhoto(currentId);
          toast.success("Photo supprimée.");
        } catch {
          toast.error("Échec de la suppression.");
        }
      }
    }
    onClose?.();
  };

  return isOpen
    ? ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={isEdit ? "Éditer une photo" : "Ajouter une photo"}
          onClick={(e) => e.currentTarget === e.target && handleClose()}
        >
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white flex items-center justify-between">
              <h3 className="font-semibold">
                {isEdit ? "Éditer la photo" : "Ajouter une photo"}
              </h3>
              <button
                onClick={handleClose}
                title="Fermer"
                aria-label="Fermer"
                className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Dropzone + Aperçu */}
              <div
                onDragOver={onDragOver}
                onDrop={onDrop}
                className="rounded-xl overflow-hidden ring-1 ring-dashed ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900"
              >
                <div
                  className="w-full bg-slate-50 dark:bg-slate-800"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <img
                    src={previewURL || FALLBACK}
                    alt="Aperçu"
                    className="w-full h-full object-contain"
                    onError={onImgError}
                  />
                </div>
                <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    Dépose une image ou{" "}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="underline font-semibold text-sky-700 dark:text-sky-400"
                    >
                      sélectionne un fichier
                    </button>
                    .
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && f.type.startsWith("image/"))
                          setSelectedFile(f);
                      }}
                    />
                    <Button
                      text={uploading ? "Téléversement…" : "Téléverser l’image"}
                      icon={FaUpload}
                      onClick={handleUploadImage}
                      className="rounded-xl gap-2"
                      disabled={uploading || !selectedFile}
                    />
                  </div>
                </div>
              </div>

              {/* URL (readonly) — toujours côté serveur */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de l’image (serveur)
                </label>
                <input
                  ref={firstFieldRef}
                  type="text"
                  value={toAbsPhoto(photoUrl)}
                  readOnly
                  placeholder="Visible après téléversement"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Légende / Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Ex. Podium Minimes — Merci aux bénévoles !"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Ligne Position + Couverture + Visibilité */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Position dans l’album
                  </label>
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    placeholder={
                      loadingDefaults
                        ? "Chargement…"
                        : "Laisser vide = position suivante"
                    }
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Laisse vide pour laisser le système choisir la prochaine
                    position.
                  </p>
                </div>

                {/* Couverture (création uniquement) */}
                {/* <div className="sm:col-span-1">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Photo de couverture
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsCover((v) => !v)}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${isCover ? "bg-amber-400" : "bg-slate-300 dark:bg-slate-700"}`}
                    aria-pressed={isCover}
                    aria-label="Basculer couverture"
                    disabled={isEdit} // on gère la couverture via l’album (étoile)
                    title={isEdit ? "Modifiable depuis l’album" : (isCover ? "Décocher couverture" : "Définir comme couverture")}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${isCover ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                  {isEdit && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Pour changer la couverture, utilise l’étoile sur la page de l’album.
                    </p>
                  )}
                </div> */}

                {/* Visibilité */}
                <div className="sm:col-span-1">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Visibilité
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsActive((v) => !v)}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
                      isActive
                        ? "bg-emerald-500"
                        : "bg-slate-300 dark:bg-slate-700"
                    }`}
                    aria-pressed={isActive}
                    aria-label="Basculer l’état actif"
                    title={isActive ? "Photo visible" : "Photo masquée"}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                        isActive ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
                <Button
                  text="Enregistrer les infos"
                  icon={FaSave}
                  onClick={handleSaveInfos}
                  className="gap-2 rounded-xl"
                  disabled={saving}
                />
                <Button
                  text="Terminer"
                  onClick={handleFinish}
                  icon={FaTimes}
                  className="gap-2 rounded-xl"
                />
              </div>

              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Astuce : si tu veux que la position soit respectée, crée
                l’entrée (ou clique “Téléverser l’image” qui crée d’abord
                l’entrée), puis l’upload associe le fichier à cette photo.
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
}
